import { Component, DestroyRef, inject, Input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, tap } from 'rxjs';
import { LoginInfoFormDialog } from '../../../login-info/login-info-form-dialog/login-info-form-dialog';
import { LoginInfoService } from '../../../login-info/login-info.service';
import { LoginInfo } from '../../../login-info/login-info.type';

@Component({
  selector: 'app-email-login-field',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './email-login-field.html',
  styleUrl: './email-login-field.scss'
})
export class EmailLoginField implements OnInit {
  private readonly _loginInfoService = inject(LoginInfoService);
  private readonly _dialog = inject(MatDialog);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _destroyRef = inject(DestroyRef);

  @Input({ required: true }) control!: FormControl<string>;
  @Input() appearance: MatFormFieldAppearance = 'outline';

  readonly loginInfoChange = output<LoginInfo | null>();

  loginInfo: LoginInfo | null = null;
  loading = false;

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(
        tap(() => { this.loading = true; }),
        debounceTime(1000),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(value => {
        if (value) {
          this.fetchLoginInfo(value);
        } else {
          this.loading = false;
          this.setLoginInfo(null);
        }
      });

    if (this.control.value) {
      this.fetchLoginInfo(this.control.value);
    }
  }

  openAddDialog(): void {
    const isMobile = window.innerWidth <= 768;
    this._dialog.open(LoginInfoFormDialog, {
      width: isMobile ? '100vw' : '600px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { mode: 'create', email: this.control.value || undefined },
    }).afterClosed().subscribe((result: LoginInfo | null) => {
      if (result) {
        this.control.setValue(result.email, { emitEvent: false });
        this.setLoginInfo(result);
        this.loading = false;
      }
    });
  }

  openEditDialog(): void {
    const isMobile = window.innerWidth <= 768;
    this._dialog.open(LoginInfoFormDialog, {
      width: isMobile ? '100vw' : '600px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { mode: 'edit', loginInfo: this.loginInfo },
    }).afterClosed().subscribe((result: LoginInfo | null) => {
      if (result) {
        this.control.setValue(result.email, { emitEvent: false });
        this.setLoginInfo(result);
        this.loading = false;
      }
    });
  }

  private fetchLoginInfo(email: string): void {
    this.loading = true;
    this._loginInfoService.getLoginInfo(email).subscribe({
      next: loginInfo => {
        this.setLoginInfo(loginInfo);
        this.loading = false;
      },
      error: err => {
        this.setLoginInfo(null);
        if (err.status !== 404) {
          this._snackBar.open('Error checking email', 'Close', { duration: 5000 });
        }
        this.loading = false;
      },
    });
  }

  private setLoginInfo(info: LoginInfo | null): void {
    this.loginInfo = info;
    this.loginInfoChange.emit(info);
  }
}
